'use client';

import { c as _c } from "react/compiler-runtime";
import { Bell, BellOff, Check, Clock, MoreHorizontal, Undo2 } from 'lucide-react';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { GlassPopoverContent } from '@/shared/ui/glass';
import { cn } from '@/lib/utils';
import { VIEWER_ID, actionsOf, type NotificationRow } from '../../model/notification-row';
import { kindByKey } from '../../model/notification-kinds';
import { useNotificationStore } from '../../model/notification-store';
import type { NotificationThread } from '../../lib/threading';
import { relativeLabel } from '../../lib/relative-time';
import { hrefFor } from '../../lib/links';
import { railToneOf, type RailTone } from '../../lib/days';
import { ClientTile } from '../client-tile';

/**
 * One line in the day.
 *
 * A row is a moment: one event, or the repeats of one event on one subject
 * folded together with the latest on the face of it. Acting on the row acts on
 * every event behind it, which is what makes "the third chase is one line"
 * safe rather than lossy.
 *
 * The face carries four things and no more: what happened, who it is about,
 * when, and a tone. Everything else, including the count of repeats and every
 * verb that is not finish-it-here, is one press away. That is the
 * progressive-disclosure rule applied to a row rather than a screen.
 */

const RAIL: Record<Exclude<RailTone, null>, string> = {
  rose: 'bg-rose-400',
  amber: 'bg-amber-400',
  emerald: 'bg-emerald-400',
  brand: 'bg-[#5651ff]'
};

/**
 * Whose initials overlap the client tile.
 *
 * A colleague, and only a colleague. The sync, the agent and the system are not
 * people, and the broker themselves is not news, so all three get no overlay:
 * the presence of a face means somebody is waiting on you.
 */
const colleagueOf = (n: NotificationRow): string | null => n.actor.kind === 'human' && n.actor.id !== VIEWER_ID ? n.actor.name : null;
const clockLabel = (iso: string): string => new Date(iso).toLocaleTimeString('en-AU', {
  hour: '2-digit',
  minute: '2-digit'
});
export interface NotificationRowProps {
  readonly thread: NotificationThread;
  /** Dense: no body copy, actions on hover. */
  readonly dense?: boolean;
  /** Show the clock rather than "2h ago". True inside an open day. */
  readonly clock?: boolean;
  /** This subject's client has an unfinished `now` on it somewhere. */
  readonly onFire?: boolean;
}
export function NotificationRow(t0) {
  const $ = _c(70);
  const {
    thread,
    dense: t1,
    clock: t2,
    onFire: t3
  } = t0;
  const dense = t1 === undefined ? false : t1;
  const clock = t2 === undefined ? false : t2;
  const onFire = t3 === undefined ? false : t3;
  const markThreadRead = useNotificationStore(_temp);
  const markThreadDone = useNotificationStore(_temp2);
  const head = thread.head;
  const tone = railToneOf(head);
  let t4;
  if ($[0] !== head) {
    const actions = actionsOf(head);
    t4 = actions.includes("done") || actions.includes("acknowledge");
    $[0] = head;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const finishable = t4;
  const unread = thread.unreadCount > 0;
  const t5 = unread ? "hover:bg-white/70 dark:hover:bg-white/12" : "opacity-[0.82] hover:opacity-100 hover:bg-white/60 dark:hover:bg-white/6";
  let t6;
  if ($[2] !== t5) {
    t6 = cn("group relative flex gap-2 rounded-xl px-2 py-1.5 transition-colors", t5);
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const t7 = tone ? RAIL[tone] : "bg-transparent";
  let t8;
  if ($[4] !== t7) {
    t8 = cn("mt-1 w-[3px] shrink-0 rounded-full", t7);
    $[4] = t7;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== t8) {
    t9 = <span aria-hidden={true} className={t8} />;
    $[6] = t8;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const t10 = head.clientId;
  const t11 = head.clientName;
  let t12;
  if ($[8] !== head) {
    t12 = colleagueOf(head);
    $[8] = head;
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  let t13;
  if ($[10] !== head.clientId || $[11] !== head.clientName || $[12] !== t12) {
    t13 = <ClientTile clientId={t10} label={t11} person={t12} size={24} />;
    $[10] = head.clientId;
    $[11] = head.clientName;
    $[12] = t12;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== head) {
    t14 = hrefFor(head);
    $[14] = head;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== markThreadRead || $[17] !== thread.key) {
    t15 = () => markThreadRead(thread.key);
    $[16] = markThreadRead;
    $[17] = thread.key;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  const t16 = unread ? "font-semibold" : "font-medium";
  let t17;
  if ($[19] !== t16) {
    t17 = cn("truncate text-[13px] leading-tight text-[#091E42] dark:text-white hover:underline", t16);
    $[19] = t16;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  let t18;
  if ($[21] !== head.title || $[22] !== t14 || $[23] !== t15 || $[24] !== t17) {
    t18 = <a href={t14} onClick={t15} className={t17}>{head.title}</a>;
    $[21] = head.title;
    $[22] = t14;
    $[23] = t15;
    $[24] = t17;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== clock || $[27] !== head.createdAt) {
    t19 = clock ? clockLabel(head.createdAt) : relativeLabel(head.createdAt);
    $[26] = clock;
    $[27] = head.createdAt;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== t19) {
    t20 = <span className="ml-auto shrink-0 text-[11px] tabular-nums text-[#44546F]/70 dark:text-white/50">{t19}</span>;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== t18 || $[32] !== t20) {
    t21 = <div className="flex items-baseline gap-2">{t18}{t20}</div>;
    $[31] = t18;
    $[32] = t20;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] !== onFire) {
    t22 = onFire ? <span aria-hidden={true} title="This client has something overdue on it" className="h-1.5 w-1.5 shrink-0 rounded-full bg-rose-400" /> : null;
    $[34] = onFire;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  const t23 = head.clientName ?? "The firm and the system";
  let t24;
  if ($[36] !== t23) {
    t24 = <span className="truncate">{t23}</span>;
    $[36] = t23;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  let t25;
  if ($[38] !== thread.count) {
    t25 = thread.count > 1 ? <span className="shrink-0 rounded-full bg-white dark:bg-white/12 px-1.5 text-[10px] font-medium text-[#091E42] dark:text-white shadow-[0_1px_3px_rgba(15,23,42,0.08)]">{thread.count === 3 ? "3rd time" : `${thread.count} times`}</span> : null;
    $[38] = thread.count;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] !== t22 || $[41] !== t24 || $[42] !== t25) {
    t26 = <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] text-[#44546F] dark:text-white/70">{t22}{t24}{t25}</div>;
    $[40] = t22;
    $[41] = t24;
    $[42] = t25;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] !== dense || $[45] !== head.body) {
    t27 = !dense && head.body ? <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-[#44546F]/85 dark:text-white/60">{head.body}</p> : null;
    $[44] = dense;
    $[45] = head.body;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] !== t21 || $[48] !== t26 || $[49] !== t27) {
    t28 = <div className="min-w-0 flex-1">{t21}{t26}{t27}</div>;
    $[47] = t21;
    $[48] = t26;
    $[49] = t27;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  const t29 = dense ? "opacity-0 group-hover:opacity-100 focus-within:opacity-100" : "opacity-100";
  let t30;
  if ($[51] !== t29) {
    t30 = cn("flex shrink-0 items-start gap-1 pt-0.5 transition-opacity", t29);
    $[51] = t29;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== finishable || $[54] !== markThreadDone || $[55] !== thread.count || $[56] !== thread.key) {
    t31 = finishable ? <button type="button" onClick={() => markThreadDone(thread.key)} title={thread.count > 1 ? `Finish all ${thread.count}` : "Finish this"} className="flex h-6 w-6 items-center justify-center rounded-full bg-white dark:bg-white/12 text-[#44546F] dark:text-white/70 shadow-[0_1px_3px_rgba(15,23,42,0.08)] transition-shadow hover:text-[#091E42] dark:hover:text-white hover:shadow-[0_2px_8px_rgba(86,81,255,0.15)]"><Check className="h-3 w-3" /></button> : null;
    $[53] = finishable;
    $[54] = markThreadDone;
    $[55] = thread.count;
    $[56] = thread.key;
    $[57] = t31;
  } else {
    t31 = $[57];
  }
  let t32;
  if ($[58] !== thread) {
    t32 = <RowMenu thread={thread} />;
    $[58] = thread;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] !== t30 || $[61] !== t31 || $[62] !== t32) {
    t33 = <div className={t30}>{t31}{t32}</div>;
    $[60] = t30;
    $[61] = t31;
    $[62] = t32;
    $[63] = t33;
  } else {
    t33 = $[63];
  }
  let t34;
  if ($[64] !== t13 || $[65] !== t28 || $[66] !== t33 || $[67] !== t6 || $[68] !== t9) {
    t34 = <div className={t6}>{t9}{t13}{t28}{t33}</div>;
    $[64] = t13;
    $[65] = t28;
    $[66] = t33;
    $[67] = t6;
    $[68] = t9;
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  return t34;
}

/**
 * Everything the face does not carry.
 *
 * Read and unread, snooze until a moment rather than for a duration, the three
 * mute scopes, and clearing a whole record at once. Each one is a real store
 * verb, so muting the renewals board here empties it out of every day and out
 * of the bell as well.
 */
function _temp2(s_0) {
  return s_0.markThreadDone;
}
function _temp(s) {
  return s.markThreadRead;
}
function RowMenu(t0) {
  const $ = _c(56);
  const {
    thread
  } = t0;
  const store = useNotificationStore();
  const head = thread.head;
  let t1;
  if ($[0] !== head.kind) {
    t1 = kindByKey(head.kind);
    $[0] = head.kind;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const kind = t1;
  const anyUnread = thread.unreadCount > 0;
  let t2;
  if ($[2] !== thread.items) {
    t2 = thread.items.some(_temp3);
    $[2] = thread.items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const snoozed = t2;
  let t3;
  if ($[4] !== store || $[5] !== thread.items) {
    t3 = ms => {
      const until = new Date(Date.now() + ms).toISOString();
      for (const n_0 of thread.items) {
        store.snooze(n_0.id, until);
      }
    };
    $[4] = store;
    $[5] = thread.items;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const snoozeAll = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <PopoverTrigger asChild={true}><button type="button" aria-label="More on this row" className="flex h-6 w-6 items-center justify-center rounded-full bg-white dark:bg-white/12 text-[#44546F] dark:text-white/70 shadow-[0_1px_3px_rgba(15,23,42,0.08)] transition-shadow hover:text-[#091E42] dark:hover:text-white hover:shadow-[0_2px_8px_rgba(86,81,255,0.15)] data-[state=open]:text-[#091E42] dark:data-[state=open]:text-white data-[state=open]:shadow-[0_2px_8px_rgba(86,81,255,0.15)]"><MoreHorizontal className="h-3 w-3" /></button></PopoverTrigger>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = anyUnread ? Check : Undo2;
  const t6 = anyUnread ? "Mark as seen" : "Put it back to unread";
  let t7;
  if ($[8] !== anyUnread || $[9] !== store || $[10] !== thread.items || $[11] !== thread.key) {
    t7 = () => anyUnread ? store.markThreadRead(thread.key) : thread.items.forEach(n_1 => store.markUnread(n_1.id));
    $[8] = anyUnread;
    $[9] = store;
    $[10] = thread.items;
    $[11] = thread.key;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t6 || $[15] !== t7) {
    t8 = <MenuItem icon={t5} label={t6} onClick={t7} />;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== store || $[18] !== thread.items) {
    t9 = thread.items.some(_temp4) ? <MenuItem icon={Undo2} label="Not finished after all" onClick={() => thread.items.forEach(n_2 => store.markUndone(n_2.id))} /> : null;
    $[17] = store;
    $[18] = thread.items;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <p className="mt-1.5 px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#44546F]/70 dark:text-white/50">Come back</p>;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== snoozed || $[22] !== store || $[23] !== thread.items) {
    t11 = snoozed ? <MenuItem icon={Bell} label="Bring it back now" onClick={() => thread.items.forEach(n_4 => store.unsnooze(n_4.id))} /> : null;
    $[21] = snoozed;
    $[22] = store;
    $[23] = thread.items;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== snoozeAll) {
    t12 = <MenuItem icon={Clock} label="After lunch" onClick={() => snoozeAll(14400000)} />;
    $[25] = snoozeAll;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== snoozeAll) {
    t13 = <MenuItem icon={Clock} label="Tomorrow morning" onClick={() => snoozeAll(72000000)} />;
    $[27] = snoozeAll;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== snoozeAll) {
    t14 = <MenuItem icon={Clock} label="Next week" onClick={() => snoozeAll(604800000)} />;
    $[29] = snoozeAll;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <p className="mt-1.5 px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#44546F]/70 dark:text-white/50">Stop hearing about</p>;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== kind || $[33] !== store) {
    t16 = kind ? <MenuItem icon={BellOff} label={kind.label} onClick={() => store.mute({
      scope: "kind",
      target: kind.key,
      until: null
    })} /> : null;
    $[32] = kind;
    $[33] = store;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== head.clientId || $[36] !== head.clientName || $[37] !== store) {
    t17 = head.clientId ? <MenuItem icon={BellOff} label={head.clientName ?? "This client"} onClick={() => store.mute({
      scope: "client",
      target: head.clientId,
      until: null
    })} /> : null;
    $[35] = head.clientId;
    $[36] = head.clientName;
    $[37] = store;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== head.boardId || $[40] !== store) {
    t18 = head.boardId ? <MenuItem icon={BellOff} label="This board" onClick={() => store.mute({
      scope: "board",
      target: head.boardId,
      until: null
    })} /> : null;
    $[39] = head.boardId;
    $[40] = store;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== head.subject.id || $[43] !== store) {
    t19 = <MenuItem icon={Check} label="Clear everything on this record" onClick={() => store.dismissRecord(head.subject.id)} />;
    $[42] = head.subject.id;
    $[43] = store;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t11 || $[46] !== t12 || $[47] !== t13 || $[48] !== t14 || $[49] !== t16 || $[50] !== t17 || $[51] !== t18 || $[52] !== t19 || $[53] !== t8 || $[54] !== t9) {
    t20 = <Popover>{t4}<GlassPopoverContent align="end" className="w-60 p-1.5">{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}</GlassPopoverContent></Popover>;
    $[45] = t11;
    $[46] = t12;
    $[47] = t13;
    $[48] = t14;
    $[49] = t16;
    $[50] = t17;
    $[51] = t18;
    $[52] = t19;
    $[53] = t8;
    $[54] = t9;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  return t20;
}
function _temp4(n_3) {
  return n_3.doneAt !== null;
}
function _temp3(n) {
  return n.snoozedUntil !== null;
}
function MenuItem(t0) {
  const $ = _c(8);
  const {
    icon: Icon,
    label,
    onClick
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = <Icon className="h-3.5 w-3.5 shrink-0" />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="truncate">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onClick || $[5] !== t1 || $[6] !== t2) {
    t3 = <button type="button" onClick={onClick} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12px] text-[#44546F] dark:text-white/70 transition-shadow hover:bg-white dark:hover:bg-white/12 hover:text-[#091E42] dark:hover:text-white hover:shadow-[0_2px_8px_rgba(86,81,255,0.15)]">{t1}{t2}</button>;
    $[4] = onClick;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}